import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import HeaderJSON from '../../../mock/header.json';
import {
  useDeviceType,
  useBodyScrollLock,
  useEscapeClose,
  useHeaderBottom,
  useHeaderData,
  useGuestCart,
  useSearchOverlay,
} from '../../../hooks';
import {
  stripAemPrefix,
  stripAemPrefixFromObject,
  getBasketLink,
} from '../../../utils';
import { PRIMARY_BAR_HEIGHT } from '../../../constants';
import { SearchTrigger } from '../../atoms';
import { UtilityLinks, SearchOverlay } from '../../molecules';
import { BasketPopover } from '../../organisms';
import { Icon } from '@arc-ui/components';
import { ICON_BASKET } from '../../atoms/SVG';
import EEBHeaderSmallDevices from './EEBHeaderSmallDevice';
import EEBHeaderDesktop from './EEBHeaderDesktop';
import { NavigationItem } from '../../../types/ee-header';
import { HeaderProvider } from '../../../contexts';
import { useEnvVariable } from '../../../contexts/EnvironmentContext';

const HeaderContainer: React.FC = () => {
  const { device, isMobile } = useDeviceType();
  const isSmallScreen = device === 'mobile' || device === 'tablet';
  const envData = useEnvVariable();
  const { viteNextPublicEeHeaderUrl } = envData;
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(
    () =>
      // eslint-disable-next-line react-hooks/exhaustive-deps
      setMenuOpen(false),
    [pathname],
  );
  useEffect(() => {
    const open = () => setMenuOpen(true);
    const close = () => setMenuOpen(false);
    window.addEventListener('eeb:menu:open', open as EventListener);
    window.addEventListener('eeb:menu:close', close as EventListener);
    return () => {
      window.removeEventListener('eeb:menu:open', open as EventListener);
      window.removeEventListener('eeb:menu:close', close as EventListener);
    };
  }, []);

  const apiHeaderData = useHeaderData(viteNextPublicEeHeaderUrl);
  const navItems =
    apiHeaderData?.[':items'] ||
    stripAemPrefixFromObject(HeaderJSON)?.[':items'];

  const {
    items,
    businessSegmentTitle,
    businessSegmentLinkUrl,
    personalSegmentTitle,
    personalSegmentLinkUrl,
    nav,
    logInLinkUrl,
    logInLinkTitle,
    abandonedBasketButtonText,
    abandonedBasketTitle,
    businessSegmentLargeBusinessButtonLabel,
    businessSegmentLargeBusinessLinkUrl,
    businessSegmentLargeBusinessMessage,
    businessSegmentLargeBusinessTitle,
    businessSegmentMainMessage,
    businessSegmentSmallBusinessButtonLabel,
    businessSegmentSmallBusinessLinkUrl,
    businessSegmentSmallBusinessMessage,
    businessSegmentSmallBusinessTitle,
  } = navItems?.global_masthead_copy || {};

  const basketLink = getBasketLink(
    apiHeaderData || stripAemPrefixFromObject(HeaderJSON),
    envData,
  );
  const cleanBasketPageUrl = stripAemPrefix(basketLink);
  const [basketOverlayOpen, setBasketOverlayOpen] = useState(false);
  const {
    overlayOpen,
    value,
    focused,
    setValue,
    setFocused,
    openOverlay,
    closeOverlay,
    submitSearch,
  } = useSearchOverlay();

  const headerBottom = useHeaderBottom('site-header', PRIMARY_BAR_HEIGHT);
  const { count: basketCount } = useGuestCart({ enabled: true });

  const overlayTop = isSmallScreen
    ? 0
    : (() => {
        const el = document.querySelector('#site-header .arc-UniversalHeader');
        return el
          ? Math.round(el.getBoundingClientRect().bottom)
          : PRIMARY_BAR_HEIGHT;
      })();

  const basketTriggerRef = useRef<HTMLButtonElement | null>(null);

  const closeAll = useCallback(() => {
    closeOverlay();
    setBasketOverlayOpen(false);
  }, [closeOverlay]);

  const openSearch = useCallback(() => {
    setBasketOverlayOpen(false);
    openOverlay();
  }, [openOverlay]);

  useEscapeClose(overlayOpen || basketOverlayOpen, closeAll);
  useBodyScrollLock(overlayOpen || basketOverlayOpen);

  const searchTriggerEl = <SearchTrigger onClick={openSearch} />;

  const basketEl = (
    <BasketPopover
      basketOverlayOpen={basketOverlayOpen}
      setBasketOverlayOpen={setBasketOverlayOpen}
      basketTriggerRef={basketTriggerRef}
      abandonedBasketButtonText={abandonedBasketButtonText}
      abandonedBasketTitle={abandonedBasketTitle}
      linkToBasketPage={cleanBasketPageUrl}
      isSmallScreen={isSmallScreen}
      badgeCount={basketCount}
      sheetTop={headerBottom}
      renderTrigger={(props) => (
        <button
          type="button"
          onClick={props.onClick}
          onKeyDown={props.onKeyDown}
          aria-expanded={props['aria-expanded']}
          aria-label={props['aria-label']}
          style={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
          }}
        >
          <Icon icon={ICON_BASKET} label="Basket" size={24} />
          {basketCount > 0 && (
            <span
              aria-label={`${basketCount} items in basket`}
              style={{
                position: 'absolute',
                top: -4,
                right: -6,
                minWidth: 16,
                height: 16,
                borderRadius: '50%',
                background: '#ebf304',
                color: '#000',
                fontSize: 10,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
              }}
            >
              {basketCount}
            </span>
          )}
        </button>
      )}
    />
  );

  const utilityLinksEl = <UtilityLinks />;

  const headerContextValue = {
    items,
    businessSegmentTitle,
    personalSegmentTitle,
    personalSegmentLinkUrl,
    businessSegmentLinkUrl,
    nav: nav as Record<string, NavigationItem> | undefined,
    logInLinkUrl,
    logInLinkTitle,
    businessSegmentLargeBusinessButtonLabel,
    businessSegmentLargeBusinessLinkUrl,
    businessSegmentLargeBusinessMessage,
    businessSegmentLargeBusinessTitle,
    businessSegmentMainMessage,
    businessSegmentSmallBusinessButtonLabel,
    businessSegmentSmallBusinessLinkUrl,
    businessSegmentSmallBusinessMessage,
    businessSegmentSmallBusinessTitle,
  };

  const commonHeaderProps = {
    overlayOpen,
    searchTriggerEl,
    basketEl,
    utilityLinksEl,
    isMobile,
  };

  return (
    <HeaderProvider value={headerContextValue}>
      <header id="site-header" role="banner">
        {isSmallScreen ? (
          <EEBHeaderSmallDevices
            {...commonHeaderProps}
            menuOpen={menuOpen}
            onOpenMenu={() => setMenuOpen(true)}
            onCloseMenu={() => setMenuOpen(false)}
          />
        ) : (
          <EEBHeaderDesktop {...commonHeaderProps} />
        )}

        {basketOverlayOpen && !overlayOpen && (
          <div
            className="eeb-SearchOverlay-dim eeb-SearchOverlay-dim--dark"
            style={{ top: headerBottom, zIndex: 80 }}
            aria-hidden="true"
            onClick={() => setBasketOverlayOpen(false)}
          />
        )}

        <SearchOverlay
          isOpen={overlayOpen}
          onClose={closeAll}
          onSearch={submitSearch}
          overlayTop={overlayTop}
          isMobile={isMobile}
          value={value}
          focused={focused}
          onValueChange={setValue}
          onFocusChange={setFocused}
          onSubmit={submitSearch}
        />
      </header>
    </HeaderProvider>
  );
};

export default HeaderContainer;
